import axios from "axios";
import { message } from "antd";

// Create axios instance with custom config
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Important for cookies
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from sessionStorage
    const token = sessionStorage.getItem("token");

    // If token exists, add it to request headers
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful response for debugging
    return response.data;
  },
  (error) => {
    // Handle different error scenarios
    if (error.response) {
      const errorMessage = error.response.data?.message || "An error occurred";
      const isLoginRequest =
        error.config.url.includes("/auth/user/login") ||
        error.config.url.includes("/auth/admin/login");

      switch (error.response.status) {
        case 400:
          message.error(
            errorMessage || "Bad request. Please check your input."
          );
          break;
        case 401:
          // Only redirect to login if it's not a login request and there's a token
          if (!isLoginRequest && sessionStorage.getItem("token")) {
            message.error("Session expired. Please login again.");
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("userInfo");
            window.location.href = "/";
          }
          break;
        case 403:
          message.error(
            errorMessage || "Forbidden. You do not have permission."
          );
          break;
        case 404:
          message.error(errorMessage || "Resource not found.");
          break;
        case 500:
          message.error(
            errorMessage || "Server error. Please try again later."
          );
          break;
        default:
          message.error(errorMessage || "An error occurred. Please try again.");
      }
    } else if (error.request) {
      message.error("No response from server. Please check your connection.");
    } else {
      message.error("Error in making request. Please try again.");
    }

    return Promise.reject(error);
  }
);

// API endpoints
export const endpoints = {
  auth: {
    adminLogin: "/auth/admin/login",
    userLogin: "/auth/user/login",
    register: "/auth/register",
    refreshToken: "/auth/refresh-token",
    logout: "/auth/logout",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
    verifyEmail: "/auth/verify-email",
    loginWithGoogle: "/auth/google/login",
  },
  user: {
    profile: "/users/profile",
    updateProfile: "/users/update-profile",
    changePassword: "/users/change-password",
  },
  payment: {
    createPaymentLink: "/payment/create-payment-link",
    checkStatus: (orderCode) => `/payment/check-status/${orderCode}`,
    orders: "/payment/orders",
    orderDetail: (orderId) => `/payment/orders/${orderId}`,
    myOrders: "/payment/my-orders",
  },
  transactionHistory: {
    checkStatus: (orderCode) => `/transaction-histories/status/${orderCode}`,
    myTransactions: "/transaction-histories/my-transactions",
    all: "/transaction-histories", // Admin only
    stats: "/transaction-histories/stats", // Admin only
    updateStatus: (orderCode) =>
      `/transaction-histories/${orderCode}/update-status`,
  },
  upload: "/upload",
  recipes: {
    list: "/recipes",
    detail: (id) => `/recipes/${id}`,
    bySlug: (slug) => `/recipes/${slug}/by-slug`,
    create: "/recipes",
    update: (id) => `/recipes/${id}`,
    delete: (id) => `/recipes/${id}`,
    deleteMany: "/recipes/delete-many",
    topRated: "/recipes?sort=-rating,-views",
    topViewed: "/recipes?sort=-views,-rating",
    getByCategory: (cate) => `/recipes/category/${cate}`,
  },
  menus: {
    list: "/menus",
    detail: (id) => `/menus/${id}`,
    bySlug: (slug) => `/menus/${slug}/by-slug`,
    create: "/menus",
    update: (id) => `/menus/${id}`,
    delete: (id) => `/menus/${id}`,
    deleteMany: "/menus/delete-many",
  },
  blogs: {
    list: "/blogs",
    detail: (id) => `/blogs/${id}`,
    bySlug: (slug) => `/blogs/${slug}/by-slug`,
    create: "/blogs",
    update: (id) => `/blogs/${id}`,
    delete: (id) => `/blogs/${id}`,
    deleteMany: "/blogs/delete-many",
    topViews: "/blogs?sort=-views&limit=4",
  },
  blogCategories: {
    list: "/blog-categories",
    detail: (id) => `/blog-categories/${id}`,
    bySlug: (slug) => `/blog-categories/${slug}/by-slug`,
    create: "/blog-categories",
    update: (id) => `/blog-categories/${id}`,
    delete: (id) => `/blog-categories/${id}`,
    deleteMany: "/blog-categories/delete-many",
  },
  subscriptions: {
    list: "/subscriptions",
    detail: (id) => `/subscriptions/${id}`,
    create: "/subscriptions",
    update: (id) => `/subscriptions/${id}`,
    delete: (id) => `/subscriptions/${id}`,
  },
  orders: {
    list: "/orders",
    detail: (id) => `/orders/${id}`,
    create: "/orders/create",
    myOrders: "/orders/myorders",
    checkSubscription: "/orders/check-subscription",
    updateStatus: (id) => `/orders/${id}/status`,
  },
  feedback: {
    byRecipe: (recipeId) => `/feedback/${recipeId}`,
    create: "/feedback",
  },
  ai: {
    suggestRecipe: "/gemini-ai/suggest-meal",
  },
};

// Helper functions for common API calls
export const api = {
  get: (url, config = {}) => axiosInstance.get(url, config),
  post: (url, data = {}, config = {}) => axiosInstance.post(url, data, config),
  put: (url, data = {}, config = {}) => axiosInstance.put(url, data, config),
  delete: (url, config = {}) => axiosInstance.delete(url, config),
  patch: (url, data = {}, config = {}) =>
    axiosInstance.patch(url, data, config),
};

export default axiosInstance;
